import { BellRing, Check } from 'lucide-react';
import { useState } from 'react';

import { cn } from '@/design-system/lib/cn';
import { Alert } from '@/design-system/ui/Alert';
import { Button } from '@/design-system/ui/Button';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from '@/design-system/ui/Dialog';
import { Input } from '@/design-system/ui/Input';
import { formatDayMonth } from '@escala/domain/dates';
import { editGroupOf } from '@escala/domain/eventEdit';
import {
  MAX_SKIP_REASON,
  SKIP_REASONS,
  cancellationAudience,
  skipReasonError,
  skipsDate,
} from '@escala/domain/skippedDates';
import { type EventScope, eventsInScope } from '@escala/domain/transitions';
import type { ScheduleEvent } from '@escala/domain/types';
import { useStore } from '@/store/AppStore';

/**
 * Excluir uma data de um evento com mais de uma — como no Google e no iOS: só esta data,
 * esta e as próximas (encerra a série nela; o que já passou fica) ou a série toda. Cada
 * opção diz quantas datas leva e avisa quando apaga histórico (quem serviu). "Esta e as
 * próximas" só existe na série que se repete; no evento sem data fixa, é esta ou todas.
 *
 * "Só esta data" na série que se repete é o culto do feriado: a série continua, a data fica
 * guardada como excluída (com o motivo, opcional) e não volta ao estender o término nem nas
 * ocorrências passadas; quem estava na escala publicada é avisado do cancelamento.
 */
export default function DeleteEventDialog({
  event,
  initial = 'one',
  onClose,
}: {
  event: ScheduleEvent;
  initial?: EventScope;
  onClose: () => void;
}) {
  const { state, today, actions } = useStore();
  const [scope, setScope] = useState<EventScope>(initial);
  const [reason, setReason] = useState('');
  const recurring = !!event.recurrence;
  const day = formatDayMonth(event.date);
  const scopes: EventScope[] = recurring ? ['one', 'following', 'series'] : ['one', 'series'];
  const skips = skipsDate(state.events, event);
  const audience = cancellationAudience(event, today).length;
  const skipping = scope === 'one' && skips;
  // O motivo só vale para "Só esta data" na série.
  const reasonError = skipping ? skipReasonError(reason) : null;

  const describe = (s: EventScope) => {
    const dates = eventsInScope(state.events, event, s);
    const n = dates.length;
    // Histórico é só o que já aconteceu com quem serviu — a escala de amanhã não conta.
    const history = dates.filter((e) => editGroupOf(e, today) === 'pastServed').length;
    const count = `${n} ${n === 1 ? 'data' : 'datas'}`;
    const loses = history
      ? ` Apaga o histórico de ${history} ${history === 1 ? 'data que já aconteceu' : 'datas que já aconteceram'}.`
      : '';
    const texts: Record<EventScope, { label: string; text: string }> = {
      one: {
        label: 'Só esta data',
        text: skips
          ? `${event.title} de ${day} e a escala dela. A série continua, e esta data não volta ao estender o término.${loses}`
          : `${event.title} de ${day} e a escala dela.${loses}`,
      },
      following: {
        label: 'Esta e as próximas',
        text: `${count} a partir de ${day}: a série termina aqui e as anteriores ficam.${loses}`,
      },
      series: {
        label: recurring ? 'Toda a série' : 'Todas as datas',
        text: `${count}, inclusive as que já passaram, com as escalas.${loses}`,
      },
    };

    return texts[s];
  };

  const confirm = () => {
    if (reasonError) return;
    actions.deleteEvent(event, scope, skipping ? reason : '');
    onClose();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-[min(480px,calc(100vw-24px))]">
        <DialogHeader
          title={recurring ? 'Excluir evento recorrente' : 'Excluir evento'}
          description={`${event.title} · ${day}`}
        />
        <DialogBody className="gap-3">
          <div role="radiogroup" aria-label="O que excluir" className="flex flex-col gap-2">
            {scopes.map((s) => {
              const on = s === scope;
              const { label, text } = describe(s);

              return (
                <button
                  key={s}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => setScope(s)}
                  className={cn(
                    'flex items-start gap-3 rounded-block border px-3.5 py-3 text-left hover:border-primary-hover-line',
                    on ? 'border-primary-line bg-primary-faint' : 'border-line-input bg-white'
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      'mt-0.5 grid size-4.5 shrink-0 place-items-center rounded-pill border-[1.5px]',
                      on ? 'border-primary bg-primary text-white' : 'border-ink-placeholder'
                    )}
                  >
                    {on ? <Check className="size-3 stroke-3" /> : null}
                  </span>
                  <span className="flex flex-col gap-0.5">
                    <span className="text-control font-semibold">{label}</span>
                    <span className="text-meta text-ink-subtle">{text}</span>
                  </span>
                </button>
              );
            })}
          </div>
          {skipping ? (
            <div className="flex flex-col gap-2">
              <label className="flex flex-col gap-1.5 text-meta font-semibold text-ink-secondary">
                Motivo (opcional)
                <Input
                  value={reason}
                  maxLength={MAX_SKIP_REASON + 10}
                  placeholder="Ex.: Feriado"
                  invalid={!!reasonError}
                  onChange={(e) => setReason(e.target.value)}
                />
              </label>
              <div className="flex flex-wrap gap-1.5" role="group" aria-label="Motivos comuns">
                {SKIP_REASONS.map((r) => (
                  <button
                    key={r}
                    type="button"
                    aria-pressed={reason === r}
                    onClick={() => setReason(reason === r ? '' : r)}
                    className={cn(
                      'h-7 rounded-pill border px-2.75 text-meta font-medium',
                      reason === r
                        ? 'border-primary bg-primary text-white'
                        : 'border-line-input bg-white text-ink-secondary hover:bg-surface-subtle'
                    )}
                  >
                    {r}
                  </button>
                ))}
              </div>
              {reasonError ? (
                <p role="alert" className="m-0 text-meta text-status-conflict">
                  {reasonError}
                </p>
              ) : null}
              {audience ? (
                <Alert tone="info">
                  <BellRing aria-hidden />
                  {audience === 1
                    ? '1 pessoa escalada recebe o aviso de que o evento foi cancelado.'
                    : `${audience} pessoas escaladas recebem o aviso de que o evento foi cancelado.`}
                </Alert>
              ) : null}
            </div>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="secondary">Cancelar</Button>
          </DialogClose>
          <Button variant="destructive" disabled={!!reasonError} onClick={confirm}>
            {skipping ? 'Excluir esta data' : 'Excluir'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
